import React, {useState} from "react";
import {Card, Col, Row, Tabs, Tag, Button, message} from "antd";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";
import {WalletSummary} from "./walletSummary/WalletSummary";
import {
    TabsContainer,
    ActionButtonWrapper,
    DateRangePickerWrapper,
    WalletInfoRow
} from "./CourierWallet.styles";
import {CourierWalletSubTab} from "./courierWalletSubTab/CourierWalletSubTab";
import {useCourierWallet} from "./useCourierWallet";
import {CourierWalletInvoiceSubTab} from "./courierWalletInvoiceSubTab/CourierWalletInvoiceSubTab";
import {CourierWalletPaymentSubTab} from "./courierWalletPaymentSubTab/CourierWalletPaymentSubTab";
import {BlockWithdrawButton} from "./blockwithdrawButton/BlockWithdrawButton";
import {WalletStartDateButton} from "./walletStartDateButton/WalletStartDateButton";
import {ActivateWalletButton} from "./activateWalletButton/ActivateWalletButton";
import {DeactivateWalletButton} from "./deactivateWalletButton/DeactivateWalletButton";
import {RetryInvoiceAccountButton} from "./retryInvoiceAccountButton/RetryInvoiceAccountButton";
import {DatePicker} from "antd";
import locale from "antd/es/date-picker/locale/tr_TR";
import moment from "moment";
import {useStore} from "@vigo/hooks";

export const CourierWallet = ({courierLoading, courier, canRender, reloadCourier}) => {
    const [subTabKey, setSubTabKey] = useState("courierWalletSubTab");
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const stores = useStore();
    const {wallet, walletLoading, refreshWallet, invoiceAccountStatus} = useCourierWallet({
        courierLoading,
        courier,
        canRender
    });
    const showWalletActionButtons =
        stores.meStore.checkRole("noAccessForHrUser") &&
        !stores.meStore.checkRole("hideForOperationRole") &&
        !stores.meStore.checkRole("hideForBmSyRole");

    const [walletStartDateRecall, setWalletStartDateRecall] = useState(false);

    const [selectedDateRange, setSelectedDateRange] = useState([
        moment().subtract(7, "days"),
        moment()
    ]);

    const handleExcelExport = async () => {
        const messageKey = "courier-wallet-excel";
        message.loading({content: "Excel hazırlanıyor...", key: messageKey});
        setLoadingExcelData(true);

        try {
            const result = await stores.walletStore.exportCourierWalletTransactions({
                courierId: courier?._id,
                startDate: selectedDateRange?.[0]?.format("YYYY-MM-DD"),
                endDate: selectedDateRange?.[1]?.format("YYYY-MM-DD")
            });

            if (!result.success) {
                message.error({
                    content: result.message || "Excel dosyası oluşturulurken bir hata oluştu.",
                    key: messageKey,
                    duration: 4
                });
                return;
            }

            // Base64 verisini blob'a çevir
            const binaryString = atob(result.data.base64Excel);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            // Dosyayı indir
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", result.data.fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success({
                content: "Excel dosyası başarıyla indirildi.",
                key: messageKey,
                duration: 4
            });
        } catch (error) {
            message.error({
                content: "Excel dosyası oluşturulurken bir hata oluştu.",
                key: messageKey,
                duration: 4
            });
        } finally {
            setLoadingExcelData(false);
        }
    };

    // Wallet yoksa hiç render olma
    if (!wallet && !walletLoading) {
        return null;
    }
    return (
        <Card loading={courierLoading || walletLoading}>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <WalletSummary
                        wallet={wallet}
                        courierId={courier?._id}
                        canEditBlockedBalance={showWalletActionButtons}
                        refreshWallet={refreshWallet}
                    />
                </Col>
                {Boolean(wallet) && !walletLoading ? (
                    <Col span={24}>
                        <WalletInfoRow>
                            <WalletStartDateButton
                                walletStartDate={wallet?.createdAt}
                                handleClick={() => setWalletStartDateRecall(!walletStartDateRecall)}
                            />
                            <Tag color="blue" style={{height: "100%", lineHeight: "30px"}}>
                                Fatura Entegratörü: <b>{courier?.vendor || "-"}</b>
                            </Tag>
                            {invoiceAccountStatus === "Yeni Başvuru" && (
                                <Tag
                                    color="green"
                                    style={{height: "100%", lineHeight: "30px", marginLeft: "8px"}}
                                >
                                    Entegratör Durumu: <b>{invoiceAccountStatus || "Durumu Yok"}</b>
                                    {stores.meStore.checkRole("noAccessForHrUser") && (
                                        <RetryInvoiceAccountButton courierId={courier?._id} />
                                    )}
                                </Tag>
                            )}
                        </WalletInfoRow>
                    </Col>
                ) : (
                    <></>
                )}
                <Col span={24} style={{marginBottom: 16}}>
                    <TabsContainer
                        onChange={setSubTabKey}
                        tabBarExtraContent={{
                            right: (
                                <VFlex gap={8} wrap align="center" fullWidth>
                                    <DateRangePickerWrapper data-date-picker-root>
                                        <DatePicker.RangePicker
                                            locale={locale}
                                            format="DD/MM/YYYY"
                                            value={selectedDateRange}
                                            onChange={setSelectedDateRange}
                                            allowClear={false}
                                            getPopupContainer={trigger =>
                                                trigger.closest("[data-date-picker-root]") ||
                                                document.body
                                            }
                                            getCalendarContainer={trigger =>
                                                trigger.closest("[data-date-picker-root]") ||
                                                document.body
                                            }
                                        />
                                    </DateRangePickerWrapper>
                                    {courier.isWalletActive ? (
                                        <ActionButtonWrapper>
                                            {showWalletActionButtons && (
                                                <DeactivateWalletButton
                                                    courier={courier}
                                                    reloadCourier={reloadCourier}
                                                    refreshWallet={refreshWallet}
                                                />
                                            )}
                                        </ActionButtonWrapper>
                                    ) : (
                                        <ActionButtonWrapper>
                                            {showWalletActionButtons && (
                                                <ActivateWalletButton
                                                    courier={courier}
                                                    reloadCourier={reloadCourier}
                                                    refreshWallet={refreshWallet}
                                                />
                                            )}
                                        </ActionButtonWrapper>
                                    )}
                                    {courier.isWalletActive && (
                                        <ActionButtonWrapper>
                                            {showWalletActionButtons && (
                                                <BlockWithdrawButton
                                                    wallet={wallet}
                                                    reloadWallet={refreshWallet}
                                                    walletLoading={walletLoading}
                                                />
                                            )}
                                        </ActionButtonWrapper>
                                    )}
                                    {courier.isWalletActive && (
                                        <ActionButtonWrapper>
                                            {showWalletActionButtons && (
                                                <Button
                                                    type="primary"
                                                    icon={<span>📊</span>}
                                                    loading={loadingExcelData}
                                                    onClick={handleExcelExport}
                                                >
                                                    Excel İndir
                                                </Button>
                                            )}
                                        </ActionButtonWrapper>
                                    )}
                                </VFlex>
                            )
                        }}
                    >
                        <Tabs.TabPane
                            tab={<span data-testid="courierWalletSubTab">Cüzdan</span>}
                            key="courierWalletSubTab"
                        >
                            <CourierWalletSubTab
                                courier={courier}
                                walletData={wallet}
                                canRender={
                                    subTabKey === "courierWalletSubTab" &&
                                    !(courierLoading || walletLoading)
                                }
                                walletStartDateRecall={walletStartDateRecall}
                                selectedDateRange={selectedDateRange}
                                onDateRangeChange={setSelectedDateRange}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            tab={<span data-testid="courierWalletInvoiceSubTab">Fatura</span>}
                            key="courierWalletInvoiceSubTab"
                        >
                            <CourierWalletInvoiceSubTab
                                courier={courier}
                                canRender={
                                    subTabKey === "courierWalletInvoiceSubTab" &&
                                    !(courierLoading || walletLoading)
                                }
                                walletStartDate={wallet?.createdAt}
                                walletStartDateRecall={walletStartDateRecall}
                                selectedDateRange={selectedDateRange}
                                onDateRangeChange={setSelectedDateRange}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            tab={<span data-testid="courierWalletPaymentSubTab">Ödeme</span>}
                            key="courierWalletPaymentSubTab"
                        >
                            <CourierWalletPaymentSubTab
                                courier={courier}
                                canRender={
                                    subTabKey === "courierWalletPaymentSubTab" &&
                                    !(courierLoading || walletLoading)
                                }
                                walletStartDate={wallet?.createdAt}
                                walletStartDateRecall={walletStartDateRecall}
                                selectedDateRange={selectedDateRange}
                                onDateRangeChange={setSelectedDateRange}
                            />
                        </Tabs.TabPane>
                    </TabsContainer>
                </Col>
            </Row>
        </Card>
    );
};
